import React, { useState } from "react";
import {
    Card, CardBody, Row, Col, Button, Label, Input, Badge
} from "reactstrap";
import DataTable from "react-data-table-component";
import api from "../../api";
import moment from "moment";

export default function Attachments({ attachments, setAttachments, epoNumber }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const handleFileSelect = (e) => {
        setSelectedFile(e.target.files[0]);
    };
    const getLocalFileURL = (fileObject) => {
        if (!fileObject) return null;
        return URL.createObjectURL(fileObject);
    };
    // Owner finding (2026-10-01, same as the DMS download buttons): a plain
    // link to the api route cannot carry the sign-in and, on the live site,
    // lands on the dashboard home. Fetch the file with the sign-in (as the
    // review modal does) and open it in a new tab.
    const openUploadedFile = async (row) => {
        try {
            const res = await api.get(`Epo/attachment/${row.id}`, { responseType: "blob" });
            const type = res.headers?.["content-type"] || res.data?.type || "application/octet-stream";
            const url = URL.createObjectURL(new Blob([res.data], { type }));
            const tab = window.open(url, "_blank", "noopener");
            if (!tab) {
                const a = document.createElement("a");
                a.href = url; a.download = row.fileName || `attachment-${row.id}`;
                document.body.appendChild(a); a.click(); a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            alert(err?.response?.data?.message || err?.response?.data?.error || "Could not open the attachment.");
        }
    };
    const handleAddAttachment = async () => {
        if (!selectedFile) {
            alert("Please select a file to upload.");
            return;
        }

        if (epoNumber) {
            setUploading(true);
            try {
                const formData = new FormData();
                formData.append("file", selectedFile);

                const res = await api.post(
                    `Epo/${epoNumber}/upload`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                setAttachments(prev => [...prev, {
                    ...res.data,
                    _uploaded: true,
                }]);
            } catch (err) {
                console.error(err);
                alert("Upload failed.");
            } finally {
                setUploading(false);
            }
        } else {
            const newAttachment = {
                id: `local_${Date.now()}`,
                fileName: selectedFile.name,
                // Batch 4 / Round 12 — NO hardcoded empId. This is only the
                // pre-save local draft shown before the EPO exists; the real
                // uploader is recorded server-side when the EPO is saved and the
                // file is actually uploaded. Show a neutral placeholder here.
                uploadedBy: null,
                uploadedByLabel: "Pending save",
                uploadedAt: new Date().toISOString(),
                fileObject: selectedFile,
                _uploaded: false,
            };
            setAttachments(prev => [...prev, newAttachment]);
        }

        setSelectedFile(null);
        document.getElementById("file-upload-input").value = "";
    };

    const handleDeleteAttachment = (id) => {
        if (!window.confirm("Are you sure you want to remove this attachment?")) return;
        setAttachments(prev => prev.filter(a => a.id !== id));
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return "ri-file-line";
        const ext = fileName.split(".").pop().toLowerCase();
        const icons = {
            pdf: "ri-file-pdf-line text-danger",
            doc: "ri-file-word-line text-primary",
            docx: "ri-file-word-line text-primary",
            xls: "ri-file-excel-line text-success",
            xlsx: "ri-file-excel-line text-success",
            jpg: "ri-image-line text-warning",
            jpeg: "ri-image-line text-warning",
            png: "ri-image-line text-warning",
        };
        return icons[ext] || "ri-file-line text-secondary";
    };

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--ssw-surface-3, #f8f9fa)",
                borderBottom: "2px solid var(--ssw-line, #dee2e6)",
                fontWeight: "600",
                fontSize: "13px",
            },
        },
        rows: {
            style: {
                fontSize: "13px",
            },
        },
    };

    const columns = [
        {
            name: "#",
            width: "50px",
            center: true,
            cell: (row, i) => <span className="text-muted">{i + 1}</span>,
        },
        {
            name: "File",
            minWidth: "220px",
            cell: (row) => {
                return (
                    <div className="d-flex align-items-center gap-2 py-1">
                        <i className={`${getFileIcon(row.fileName)} fs-5`} />
                        <div>
                            <div className="fw-semibold" style={{ fontSize: 13 }}>
                                {row.fileName}
                            </div>
                            {!row._uploaded && (
                                <Badge color="warning" pill style={{ fontSize: 10 }}>
                                    Pending Upload
                                </Badge>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Uploaded By",
            width: "130px",
            center: true,
            cell: (row) => {
                return (
                    <span>{row.uploadedBy || row.uploadedByName || row.employee || row.uploadedByLabel || "—"}</span>
                );
            },
        },
        {
            name: "Date",
            width: "180px",
            center: true,
            cell: (row) => {
                const dateStr = row.uploadedAt
                    ? moment(row.uploadedAt).format("MM/DD/YYYY hh:mm A")
                    : (row.date || "—");
                return (
                    <span style={{ fontSize: 12 }}>{dateStr}</span>
                );
            },
        },
        {
            name: "Actions",
            width: "140px",
            center: true,
            cell: (row) => {
                const isUploaded = row._uploaded && !String(row.id).startsWith("local_");
                const hasLocalFile = !isUploaded && row.fileObject;
                return (
                    <div className="d-flex gap-2">
                        {isUploaded ? (
                            <button
                                type="button"
                                onClick={() => openUploadedFile(row)}
                                className="btn btn-sm btn-outline-primary"
                                title="View file"
                            >
                                <i className="ri-eye-line" />
                            </button>
                        ) : hasLocalFile ? (
                            // Local file not yet uploaded — open from memory
                            <a
                                href={getLocalFileURL(row.fileObject)}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-outline-warning"
                                title="Preview local file (not yet uploaded)"
                            >
                                <i className="ri-eye-line" />
                            </a>
                        ) : (
                            <Button
                                size="sm"
                                color="outline-secondary"
                                title="Available after saving EPO"
                                disabled
                            >
                                <i className="ri-eye-off-line" />
                            </Button>
                        )
                        }
                        < Button
                            size="sm"
                            color="outline-danger"
                            onClick={() => handleDeleteAttachment(row.id)
                            }
                            title="Remove"
                        >
                            <i className="ri-delete-bin-line" />
                        </Button >
                    </div >
                );
            },
        },
    ];

    return (
        <Card>
            <CardBody>
                <DataTable
                    columns={columns}
                    data={attachments}
                    customStyles={customStyles}
                    noDataComponent={
                        <div className="py-4 text-center text-muted">
                            <i className="ri-attachment-line fs-3 d-block mb-1" />
                            No attachments added yet.
                        </div>
                    }
                    highlightOnHover
                    responsive
                    dense
                />

                <hr />

                <h6 className="fw-semibold mb-3">
                    <i className="ri-upload-cloud-line me-1 text-primary" />
                    Add New Attachment
                </h6>

                <Row className="align-items-end g-3">
                    <Col lg={8}>
                        <Label className="form-label">Select File</Label>
                        <Input
                            id="file-upload-input"
                            type="file"
                            onChange={handleFileSelect}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        />
                        <small className="text-muted">
                            Allowed: PDF, Word, Excel, JPG, PNG
                        </small>
                    </Col>

                    <Col lg={4} className="d-flex justify-content-end">
                        <Button
                            color="primary"
                            onClick={handleAddAttachment}
                            disabled={!selectedFile || uploading}
                        >
                            {uploading ? (
                                <><i className="ri-loader-4-line me-1" /> Uploading...</>
                            ) : (
                                <><i className="ri-upload-2-line me-1" /> Add Attachment</>
                            )}
                        </Button>
                    </Col>
                </Row>

                {!epoNumber && attachments.some(a => !a._uploaded) && (
                    <div className="alert alert-warning mt-3 mb-0 py-2" style={{ fontSize: 13 }}>
                        <i className="ri-information-line me-1" />
                        Files marked <Badge color="warning" pill>Pending Upload</Badge> will be uploaded when you save the EPO.
                    </div>
                )}
            </CardBody>
        </Card>
    );
}